import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FaQuestionCircle, FaExternalLinkAlt, FaRedo, FaKeyboard } from 'react-icons/fa';
import { useOverlay } from '../../contexts/FocusContext';
import type { GraphicsQuality } from './types';
import UnityGameDockApp from './UnityGameDockApp';

interface HelpWidgetProps {
  onReplayTour?: () => void;
  /** Opens the same keyboard / game controls overlay as the former dock “Keys” button */
  onOpenKeyboardControls?: () => void;
  graphicsQuality?: GraphicsQuality;
  autoDetectedQuality?: GraphicsQuality | null;
  onGraphicsQualityChange?: (quality: GraphicsQuality) => void;
  dockMode?: boolean;
  dataTourId?: string;
}

const QUALITY_LABELS: Record<GraphicsQuality, string> = {
  auto: 'Auto',
  high: 'High (100%)',
  medium: 'Medium (75%)',
  low: 'Low (50%)',
  performance: 'Performance (33%)'
};

const HelpWidget: React.FC<HelpWidgetProps> = ({
  onReplayTour,
  onOpenKeyboardControls,
  graphicsQuality = 'auto',
  autoDetectedQuality = null,
  onGraphicsQualityChange,
  dockMode = true,
  dataTourId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  /** Stable id for portaled dock popover (React 17 — no React 18 id hook) */
  const popoverOwnerId = useMemo(() => `dock-popover-${Math.random().toString(36).slice(2)}`, []);

  // Register as overlay when dropdown is open
  useOverlay('help-widget', isOpen);

  const handleFAQClick = () => {
    window.open('https://docs.intersectmbo.org/intersect-membership/annual-member-meetings-amm/2025-annual-members-meeting/faq', '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  const handleReplayTour = () => {
    if (onReplayTour) {
      onReplayTour();
    }
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (dropdownRef.current?.contains(t)) return;
      if (
        dockMode &&
        (t as Element).closest?.(`[data-unity-dock-popover-owner="${popoverOwnerId}"]`)
      ) {
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, dockMode, popoverOwnerId]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const menuBody = (
    <>
      {onOpenKeyboardControls && (
        <>
          <button
            type="button"
            onClick={() => {
              onOpenKeyboardControls();
              setIsOpen(false);
            }}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-white transition-colors hover:bg-gray-800"
          >
            <FaKeyboard size={14} className="text-blue-400" />
            <span className="text-sm font-medium">Keyboard and game controls</span>
          </button>
          <div className="border-t border-gray-700" />
        </>
      )}
      <button
        type="button"
        onClick={handleFAQClick}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-white transition-colors hover:bg-gray-800"
      >
        <FaExternalLinkAlt size={14} className="text-cyan-400" />
        <span className="text-sm font-medium">FAQ & Support</span>
      </button>
      <div className="border-t border-gray-700" />
      <button
        type="button"
        onClick={handleReplayTour}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-white transition-colors hover:bg-gray-800"
      >
        <FaRedo size={14} className="text-purple-400" />
        <span className="text-sm font-medium">Replay Tutorial</span>
      </button>
      {onGraphicsQualityChange && (
        <>
          <div className="border-t border-gray-700" />
          <div className="px-4 py-3">
            <label className="mb-1.5 block text-xs text-gray-400">Graphics quality</label>
            <select
              value={graphicsQuality}
              onChange={(e) => onGraphicsQualityChange(e.target.value as GraphicsQuality)}
              className="w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none"
            >
              {(['auto', 'high', 'medium', 'low', 'performance'] as const).map((q) => (
                <option key={q} value={q}>
                  {q === 'auto' && autoDetectedQuality
                    ? `Auto (currently: ${QUALITY_LABELS[autoDetectedQuality]})`
                    : QUALITY_LABELS[q]}
                </option>
              ))}
            </select>
            {graphicsQuality === 'auto' && (
              <p className="mt-1 text-xs text-gray-500">Automatically adjusts for performance.</p>
            )}
          </div>
        </>
      )}
    </>
  );

  return (
    <div ref={dropdownRef} className="relative">
      {dockMode ? (
        <UnityGameDockApp
          dataTourId={dataTourId}
          popoverOwnerId={popoverOwnerId}
          ariaLabel="Help, FAQ, and settings"
          label="Help"
          icon={<FaQuestionCircle />}
          active={isOpen}
          tileClassName="bg-gradient-to-br from-cyan-600 to-sky-700"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {isOpen && (
            <div
              className="unity-dock-popover unity-dock-popover--enter min-w-[12rem] overflow-hidden border-gray-700 bg-gray-900"
              onClick={(e) => e.stopPropagation()}
            >
              {menuBody}
            </div>
          )}
        </UnityGameDockApp>
      ) : (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className={`flex items-center gap-2 rounded bg-cyan-600 p-2 text-white shadow-lg transition-all duration-200 hover:bg-opacity-100 ${
              isOpen ? 'bg-opacity-100' : 'bg-opacity-80'
            }`}
            style={{ zIndex: 10000 }}
            title="Get Help & Support"
          >
            <FaQuestionCircle size={16} />
            <span className="text-sm font-medium">Help</span>
          </button>
          {isOpen && (
            <div
              className="animate-fadeIn absolute right-0 top-full z-[10001] mt-2 min-w-[180px] overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {menuBody}
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
      `}</style>
    </div>
  );
};

export default HelpWidget;
